import { WalletName } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import { useCallback, useEffect, useState } from 'react'
import { describeWalletError } from '../errors'

/**
 * Select a wallet and connect to it.
 *
 * `select()` only records the choice -- the adapter is swapped in on a later render, so the
 * connect call has to wait for `wallet` to catch up. All four pickers on the site grew their own
 * `useEffect` for that, each subtly different, and they raced: one fired `connect()` on mount
 * before any wallet was chosen, another retried on every render of a changing dependency. One
 * implementation, and failures come back as text instead of an unhandled rejection.
 */
export const useSelectWallet = () => {
    const { select, connect, wallet, connecting, connected, publicKey } = useWallet()
    const [pending, setPending] = useState<string>()
    const [error, setError] = useState<string>()

    useEffect(() => {
        if (!pending) return
        // Wait for the provider to actually swap the adapter in.
        if (wallet?.adapter.name !== pending) return
        if (connected || connecting) return

        let cancelled = false
        connect()
            .catch((e) => {
                if (!cancelled) setError(describeWalletError(e))
            })
            .finally(() => {
                if (!cancelled) setPending(undefined)
            })

        return () => {
            cancelled = true
        }
    }, [pending, wallet, connected, connecting, connect])

    const choose = useCallback(
        (name: string) => {
            setError(undefined)
            setPending(name)
            select(name as WalletName)
        },
        [select]
    )

    return {
        choose,
        /** Name of the wallet mid-connect, for per-button spinners. */
        pending,
        error,
        clearError: () => setError(undefined),
        wallet,
        connecting,
        connected,
        publicKey,
    }
}
